import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { statsAPI } from "@/lib/api";
import { Home, Star, MessageSquare, TrendingUp, ArrowRight, Loader2 } from "lucide-react";

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await statsAPI.get(token);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats
    ? [
        {
          title: "Total Homestays",
          value: stats.total_homestays,
          icon: Home,
          color: "bg-primary",
          link: "/admin/homestays",
        },
        {
          title: "Total Reviews",
          value: stats.total_reviews,
          subtitle: `${stats.pending_reviews} pending`,
          icon: Star,
          color: "bg-accent",
          link: "/admin/reviews",
        },
        {
          title: "Inquiries",
          value: stats.total_inquiries,
          subtitle: `${stats.pending_inquiries} pending`,
          icon: MessageSquare,
          color: "bg-green-600",
          link: "/admin/inquiries",
        },
      ]
    : [];

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1
            className="text-3xl font-semibold text-foreground mb-2"
           
          >
            Dashboard
          </h1>
          <p className="text-muted-foreground">
            Welcome to your Kokan Homestays admin dashboard
          </p>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {statCards.map((stat, index) => (
              <Link
                key={index}
                to={stat.link}
                className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-floating transition-all group"
                data-testid={`stat-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}
                  >
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <h3 className="text-4xl font-bold text-foreground mb-1">
                  {stat.value}
                </h3>
                <p className="text-muted-foreground">{stat.title}</p>
                {stat.subtitle && (
                  <p className="text-sm text-accent mt-1">{stat.subtitle}</p>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <h2
            className="text-xl font-semibold text-foreground mb-4"
           
          >
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/admin/homestays"
              className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors"
              data-testid="quick-action-homestays"
            >
              <Home className="w-5 h-5 text-primary" />
              <span className="font-medium">Manage Homestays</span>
            </Link>
            <Link
              to="/admin/reviews"
              className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors"
              data-testid="quick-action-reviews"
            >
              <Star className="w-5 h-5 text-accent" />
              <span className="font-medium">Review Moderation</span>
            </Link>
            <Link
              to="/admin/inquiries"
              className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors"
              data-testid="quick-action-inquiries"
            >
              <MessageSquare className="w-5 h-5 text-green-600" />
              <span className="font-medium">View Inquiries</span>
            </Link>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <TrendingUp className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-foreground mb-2">Pro Tip</h3>
              <p className="text-muted-foreground">
                Add high-quality images and detailed descriptions to your homestays
                to attract more bookings. Properties with complete information get
                up to 40% more inquiries!
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
